import { Theme, useTheme } from "@/components/theme/themeProvider"
import { cssColorToHex } from "@/modules/utils/colorUtils"
import type { Appearance } from "@stripe/stripe-js"
import { useMemo } from "react"

export function useStripeAppearance(): Appearance {
  const { theme, getPropertyValue } = useTheme()

  const appearance = useMemo<Appearance>(() => {
    const isDark = theme === Theme.OwnerDark

    // Read computed CSS variable values (browser converts oklch to rgb)
    const getColor = (varName: string): string => {
      const value = getPropertyValue(varName)
      return cssColorToHex(value)
    }

    // Read theme colors from CSS variables
    const colorPrimary = getColor("--primary-500")
    const colorBackground = getColor("--surface-foreground")
    const colorText = getColor("--textDefault-body")
    const colorTextSecondary = getColor("--textDefault-caption")
    const colorTextPlaceholder = getColor("--textDefault-placeholder")
    const colorDanger = getColor("--textError")
    const colorSuccess = getColor("--textSuccess")
    const colorWarning = getColor("--feedback-notice-intense")
    const borderColor = getColor("--borderDefault")
    const inputBackground = getColor("--surface-foreground")
    const colorPrimaryText = getColor("--textDefault-body")
    const borderRadius = "1rem"

    return {
      theme: isDark ? "night" : "flat",
      labels: "floating",
      variables: {
        fontFamily: "Trenda, sans-serif",
        fontSizeBase: "16px",
        fontWeightNormal: "400",
        fontWeightMedium: "500",
        fontWeightBold: "600",

        colorPrimary,
        colorBackground,
        colorText,
        colorTextSecondary,
        colorTextPlaceholder,
        colorDanger,
        colorSuccess,
        colorWarning,

        accessibleColorOnColorPrimary: colorPrimaryText,

        borderRadius: borderRadius,
        spacingUnit: "4px",
        gridRowSpacing: "16px",
        gridColumnSpacing: "16px",

        focusOutline: "none",
      },
      rules: {
        ".Input": {
          backgroundColor: inputBackground,
          border: `1px solid ${borderColor}`,
          borderRadius: borderRadius,
          padding: "12px 16px",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          boxShadow: "none",
          color: colorTextPlaceholder,
        },
        ".Input:hover": {
          borderColor: colorPrimary,
        },
        ".Input:focus": {
          borderColor: colorPrimary,
          boxShadow: `0 0 0 3px ${colorPrimary}40`,
        },
        ".Input--invalid": {
          borderColor: colorDanger,
        },
        ".Input--invalid:focus": {
          boxShadow: `0 0 0 3px ${colorDanger}40`,
        },
        ".Label": {
          fontWeight: "500",
          marginBottom: "8px",
        },
        ".Error": {
          color: colorDanger,
          fontSize: "14px",
          marginTop: "4px",
        },
        ".AccordionItem--selected": {
          backgroundColor: colorBackground,
          color: colorPrimaryText,
          borderColor: borderColor,
          borderRadius: borderRadius,
        },
      },
    }
  }, [theme, getPropertyValue])

  return appearance
}
